import { LICENSE_MODULE } from "@selector/shared";
import { SelMessage } from "@selector/shared/components";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import {
  ActionButton,
  ActionButtonIntent,
  ActionButtonSize,
  HStack,
  type OverlayPlacement,
  SymbolIcon,
  Tooltip,
  TooltipIntent,
  useDialogWithPosition,
} from "widgets";

import { capitalizeFirstLetter } from "../../app-utils/string.js";
import { type Dictionary } from "../../app-utils/type.js";
import { type Configuration } from "../../graphql.js";
import { type IAppState } from "../../redux/reducer.js";
import { COLOR } from "../../settings/colors-settings.js";
import { GETTING_STARTED_ELEMENT_ID } from "../../settings/settings.js";
import GenericCard from "../core/GenericCard.js";

interface IGettingStartedTip {
  elementId: string;
  title: string;
  content: string;
  placement?: OverlayPlacement;
}

const RESIZE_DEBOUNCE_TIMER = 200;
const gettingStartedTips: IGettingStartedTip[] = [
  {
    elementId: GETTING_STARTED_ELEMENT_ID.CONTENT_BROWSER,
    title: "Content Menu",
    content: "Open the Content menu to explore the content that is available to you in Genedata Selector",
  },
  {
    elementId: GETTING_STARTED_ELEMENT_ID.GLOBAL_SEARCH,
    title: "Quick Search",
    content: "Enter a search term to find the related data in Genedata Selector",
  },
  {
    elementId: GETTING_STARTED_ELEMENT_ID.CUSTOMIZE_DASHBOARD,
    title: "Customize Dashboard",
    content: "Define and modify the content of your dashboard screen",
  },
  {
    elementId: GETTING_STARTED_ELEMENT_ID.HELP_MENU_BUTTON,
    title: "Help and Documentation",
    content: "For more in-depth information, browse or search the comprehensive documentation library",
  },
];

const GettingStartedDashboardCard: FC = (props) => {
  const [currentVisibleId, setCurrentVisibleId] = useState<string>();
  const [isEnable, setEnable] = useState<Dictionary<boolean>>({});
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);

  const checkElementVisibility = useCallback(() => {
    gettingStartedTips.forEach((tip) => {
      const elem = document.getElementById(tip.elementId);
      const enabled = elem && elem.clientWidth > 0;

      setEnable((isEnable) => {
        isEnable[tip.elementId] = enabled;
        return isEnable;
      });
    });
  }, []);

  const onWindowResize = useDebouncedCallback(() => {
    // we should hide all tips when resizing to ensure a clean starting point afterwards
    setCurrentVisibleId(undefined);
    checkElementVisibility();
  }, RESIZE_DEBOUNCE_TIMER);

  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    checkElementVisibility();

    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, [checkElementVisibility, onWindowResize]);

  const createTipLink = useCallback(
    (elementId: string, label: string) => {
      const isEnabled = isEnable[elementId];
      return (
        <ActionButton
          label={capitalizeFirstLetter(label)}
          intent={ActionButtonIntent.Outlined}
          isDisabled={!isEnabled}
          tooltip={!isEnabled ? "This element is currently hidden because the browser window is too small" : undefined}
          onPress={() => {
            if (isEnabled) setCurrentVisibleId(elementId);
          }}
          size={ActionButtonSize.Small}
        />
      );
    },
    [isEnable],
  );

  return (
    <GenericCard {...props} tipTourId="getting-started-title">
      {/* Create the content of the getting started tooltips in the DOM */}
      {gettingStartedTips.map((tip) => (
        <TipDialog
          tip={tip}
          key={`getting-started-tip-${tip.elementId}`}
          isOpen={currentVisibleId === tip.elementId}
          reset={() => setCurrentVisibleId(undefined)}
        />
      ))}
      <section style={{ height: "100%" }}>
        <div className="getting-started-panel-content">
          <div>
            <strong>
              <SelMessage id={"dashboard.gettingStarted.welcome"} />
              <br />
              Are you wondering where to start? Here are some things you can try.
            </strong>
          </div>
          <div>
            <SelMessage
              id={"dashboard.gettingStarted.openContent"}
              values={{
                tip: ([chunk, ..._otherChunks]) => createTipLink(GETTING_STARTED_ELEMENT_ID.CONTENT_BROWSER, chunk),
              }}
            />
          </div>

          {configuration.license.modules.includes(LICENSE_MODULE.EXPLORER) && (
            <div>
              Perform a {createTipLink(GETTING_STARTED_ELEMENT_ID.GLOBAL_SEARCH, "search")} to locate specific content.
            </div>
          )}

          <div>
            {createTipLink(GETTING_STARTED_ELEMENT_ID.CUSTOMIZE_DASHBOARD, "Customize")} the content on your home
            screen, e.g. remove this card if you don't need it anymore.
          </div>
          <div>
            Have a look at the {createTipLink(GETTING_STARTED_ELEMENT_ID.HELP_MENU_BUTTON, "documentation")}, where you
            can find user guides, tutorials, tips, and more.
          </div>
        </div>
      </section>
    </GenericCard>
  );
};

interface TipDialogProps {
  tip: any;
  isOpen: boolean;
  reset: () => void;
}

const TipDialog: FC<TipDialogProps> = (props) => {
  const ref = useRef(null);
  const { dialogProps, dialogRef, arrowProps, state } = useDialogWithPosition(
    {
      placement: props.tip.placement ?? "bottom",
      onClose() {
        props.reset();
      },
    },
    ref,
  );

  useEffect(() => {
    ref.current = document.getElementById(props.tip.elementId);
  }, [props.tip.elementId]);

  useEffect(() => {
    if (props.isOpen) {
      state.open();
      // TODO: Call state.open directly?
    }
  }, [props.isOpen]);

  if (!ref.current) return;

  return (
    state.isOpen && (
      <Tooltip
        intent={TooltipIntent.Info}
        domProps={dialogProps}
        ref={dialogRef}
        arrowProps={arrowProps}
        id={`content-${props.tip.elementId}`}
        closeFn={state.close}
        title={props.tip.title}
      >
        <HStack style={{ alignItems: "flex-start" }}>
          <div style={{ fontSize: 13, lineHeight: 1.35 }}>{props.tip.content}</div>
          <SymbolIcon icon={"info"} size={30} style={{ flex: "0 0 auto", color: COLOR.WINDOWS_BLUE.NORMAL }} />
        </HStack>
      </Tooltip>
    )
  );
};

export default GettingStartedDashboardCard;
